import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchImagesByMaterialForHomePage,
  fetchMaterialIconsByProductIds,
  fetchProducts,
  persistProductSelection,
} from '../../services';
import { IProductsState, UpdateSelectedMaterialPayload } from '../../components/interfaces';

const initialState: IProductsState = {
  products: [],
  status: 'idle',
  error: null,
  currentCatalogFilter: 'all',
  materialImages: {},
  productImagesByProductId: {},
  materialIconsByProductId: {},
  imagesByMaterialForHomePage: {},
  statusByProductId: {},
  errorByProductId: {},
  skip: 0,
  hasMore: true,
};

export const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {

    updateSelectedMaterial(state, action: PayloadAction<UpdateSelectedMaterialPayload>) {
      const { productId, materialName } = action.payload;
      const product = state.products?.find(p => p.id === productId);
      if (product) {
        product.selectedMaterial = materialName;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state, action) => {
        state.status = 'loading';
        state.error = null;
        const nextFilter = action.meta.arg.filterKey ?? 'all';

        if (action.meta.arg.skip === 0 && state.currentCatalogFilter !== nextFilter) {
          state.products = [];
          state.skip = 0;
          state.hasMore = true;
          state.currentCatalogFilter = nextFilter;
          state.imagesByMaterialForHomePage = {};
          state.materialIconsByProductId = {};
          state.statusByProductId = {};
          state.errorByProductId = {};
        }
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.currentCatalogFilter = action.payload.filterKey;

        const nextProducts = action.payload.skip === 0
          ? action.payload.products
          : [
              ...state.products,
              ...action.payload.products.filter(
                (product) => !state.products.some((existing) => existing.id === product.id)
              ),
            ];

        state.products = nextProducts;
        state.skip = action.payload.skip + action.payload.products.length;
        state.hasMore = action.payload.products.length >= action.payload.take;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ? action.payload.toString() : 'Unknown error';
      })
      .addCase(fetchImagesByMaterialForHomePage.pending, (state, action) => {
        const { productId } = action.meta.arg;
        if (action.meta.arg.source === 'home') {
          state.statusByProductId[productId] = 'loading';
          state.errorByProductId[productId] = '';
        }
      })
      .addCase(fetchImagesByMaterialForHomePage.fulfilled, (state, action) => {
        const { productId, images, source } = action.payload;
        
        if (!source || source === 'home') {
          state.imagesByMaterialForHomePage[productId] = {
            images: images.map((img) => img.image.publicUrl),
            selectedMaterial: action.meta.arg.selectedMaterial,
          };
          state.statusByProductId[productId] = 'succeeded';
        }
      })
      .addCase(fetchImagesByMaterialForHomePage.rejected, (state, action) => {
        if (action.meta.arg.source === 'home') {
          const { productId } = action.meta.arg;
          state.statusByProductId[productId] = 'failed';
          state.errorByProductId[productId] = action.payload || 'Unknown error';
        }
      })
      .addCase(fetchMaterialIconsByProductIds.fulfilled, (state, action) => {
        const source = (action.meta.arg as any).source;
        if (!source || source === 'home') {
          state.materialImages = {
            ...state.materialImages,
            ...action.payload,
          };
        }
      })
      .addCase(persistProductSelection.fulfilled, (state, action) => {
        const product = state.products.find((item) => item.id === action.payload.productId);
        if (product && action.payload.selectedMaterial) {
          product.selectedMaterial = action.payload.selectedMaterial;
        }
      });
  },
});

export default productsSlice.reducer;

export const { updateSelectedMaterial } = productsSlice.actions;
